import { useState, useEffect } from "react";
import { IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent, IonList, IonItem, IonLabel, IonPage, IonIcon, IonRadioGroup, IonRadio, IonSpinner, IonFooter, IonText } from "@ionic/react";
import { close, cardOutline, sparklesOutline, checkmarkOutline, timeOutline, shieldCheckmarkOutline, infiniteOutline } from "ionicons/icons";
import { Browser } from "@capacitor/browser";
import { useDevicePaymentPlanPreviewQuery, useDevicePaymentPlanQuery } from "../query/devicePaymentPlanQuery";
import { useCreateDevicePaymentPlanCheckoutSessionMutation } from "../query/devicePaymentPlanCheckoutSessionMutation";
import { useCreateDevicePaymentCustomerPortalSessionMutation } from "../query/devicePaymentPlanCustomerPortalSessionMutation";
import useUX from "../utils/ux";
import { PaymentPlanState } from "../models/DevicePaymentPlan";
import { isPlatform } from '@ionic/react';
import ReactConfetti from 'react-confetti';
import { useModalTranslation } from '../hooks/useTranslation';
import styles from './DevicePaymentPlanModal.module.css';

const isNative = isPlatform('hybrid');
const RETURN_URL_BASE =  isNative ? 'https://onlycat.app/payment' : window.location.origin + '/payment';

const UNLOCK_KEYS = [
    'payment_plan.required.unlocks.contraband',
    'payment_plan.required.unlocks.door_policies',
    'payment_plan.required.unlocks.video',
    'payment_plan.required.unlocks.stats',
    'payment_plan.required.unlocks.support',
] as const;

const PLAN_OPTIONS = [
    {
        value: 'monthly',
        nameKey: 'payment_plan.required.plans.monthly.name',
        priceKey: 'payment_plan.required.plans.monthly.price',
        subKey: undefined,
        badgeKey: undefined,
    },
    {
        value: 'yearly',
        nameKey: 'payment_plan.required.plans.annual.name',
        priceKey: 'payment_plan.required.plans.annual.price',
        subKey: 'payment_plan.required.plans.annual.sub',
        badgeKey: 'payment_plan.required.plans.annual.badge',
    },
    {
        value: 'lifetime',
        nameKey: 'payment_plan.required.plans.lifetime.name',
        priceKey: 'payment_plan.required.plans.lifetime.price',
        subKey: 'payment_plan.required.plans.lifetime.sub',
        badgeKey: undefined,
    },
] as const;

const FEATURE_KEYS = [
    'payment_plan.features.contraband',
    'payment_plan.features.door_policies',
    'payment_plan.features.video',
    'payment_plan.features.stats',
    'payment_plan.features.support',
] as const;

const CONFETTI_COLORS = ['#8CD9A9', '#F7C948', '#7FB6E8', '#F48FB1', '#FF8A73'];

const formatAmount = (amount: number, currency: string) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
}).format(amount / 100);

const DevicePaymentPlanModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) => {
    const [selectedPriceType, setSelectedPriceType] = useState('monthly');
    const paymentPlanQuery = useDevicePaymentPlanQuery(deviceId);
    const planState = paymentPlanQuery.data?.state;
    // Only a required plan shows the plan picker; any other state has nothing to price yet.
    const previewQuery = useDevicePaymentPlanPreviewQuery(deviceId, selectedPriceType, planState === PaymentPlanState.Required);
    const lifetimeUpsellPreviewQuery = useDevicePaymentPlanPreviewQuery(deviceId, 'lifetime', planState === PaymentPlanState.Active);
    const checkoutMutation = useCreateDevicePaymentPlanCheckoutSessionMutation();
    const customerPortalMutation = useCreateDevicePaymentCustomerPortalSessionMutation();
    const { presentResult } = useUX();
    const { t: tModals } = useModalTranslation();

    // For confetti
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const celebrating = planState === PaymentPlanState.Lifetime || planState === PaymentPlanState.Active;

    useEffect(() => {
        if (celebrating) {
            const handleResize = () => {
                setDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            };

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [celebrating]);

    const handleCheckout = async (priceTypeOverride?: string) => {
        presentResult(async () => {
            const result = await checkoutMutation.mutateAsync({
                deviceId,
                priceType: priceTypeOverride || selectedPriceType,
                successUrl: RETURN_URL_BASE + '/success' + (isNative ? '' : '?deviceId=' + deviceId),
                cancelUrl: RETURN_URL_BASE + '/cancel' + (isNative ? '' : '?deviceId=' + deviceId)
            });
            if (result?.url) {
                await Browser.open({
                    url: result.url,
                    windowName: "_self"
                });
            } else {
                console.error("Checkout session creation failed", result);
                throw new Error("Failed to create checkout session");
            }
        })
    };

    const renderPendingPlanContent = () => {
        const preview = previewQuery.data;
        const inTrial = !!(preview?.trialEndDate && preview?.currentDate &&
            new Date(preview.trialEndDate) > new Date(preview.currentDate));
        const trialDays = inTrial
            ? Math.floor(Math.abs(new Date(preview!.trialEndDate).getTime() - new Date(preview!.currentDate).getTime()) / (1000 * 60 * 60 * 24))
            : 0;
        const lineItems: any[] = (!inTrial && preview?.lineItems) || [];
        const dueToday = lineItems.length
            ? lineItems.reduce((sum, line) => sum + line.amount, 0)
            : undefined;

        return (
            <>
                <IonContent color='light'>
                    <div className={styles.wrapper}>
                        <div className={styles.titleRow}>
                            <span className={styles.titleIcon}>
                                <IonIcon icon={sparklesOutline} />
                            </span>
                            <div>
                                <h2 className={styles.title}>{tModals('payment_plan.required.title')}</h2>
                                <p className={styles.lead}>{tModals('payment_plan.required.subtitle')}</p>
                            </div>
                        </div>
                        <div className={styles.unlockList}>
                            {UNLOCK_KEYS.map((key) => (
                                <div key={key} className={styles.unlockRow}>
                                    <span className={styles.unlockCheck}>
                                        <IonIcon icon={checkmarkOutline} />
                                    </span>
                                    <span>{tModals(key)}</span>
                                </div>
                            ))}
                        </div>
                        <div className={styles.kicker}>{tModals('payment_plan.required.choose_plan')}</div>
                        <IonList className={styles.listCard}>
                            <IonRadioGroup value={selectedPriceType} onIonChange={e => setSelectedPriceType(e.detail.value)}>
                                {PLAN_OPTIONS.map((plan) => (
                                    <IonItem key={plan.value} className={selectedPriceType === plan.value ? styles.planItemSelected : undefined}>
                                        <IonRadio value={plan.value} justify="space-between" className={styles.planRadio}>
                                            <div className={styles.planLabel}>
                                                <span className={styles.planTop}>
                                                    <span className={styles.planName}>{tModals(plan.nameKey)}</span>
                                                    {plan.badgeKey && <span className={styles.saveBadge}>{tModals(plan.badgeKey)}</span>}
                                                </span>
                                                <span className={styles.planPrice}>
                                                    {tModals(plan.priceKey)}
                                                    {plan.subKey && <> {'•'} {tModals(plan.subKey)}</>}
                                                </span>
                                            </div>
                                        </IonRadio>
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </IonList>
                        <div className={styles.kicker}>{tModals('payment_plan.required.billing')}</div>
                        <IonList className={styles.listCard}>
                            {previewQuery.isLoading ? (
                                <IonItem>
                                    <div className={styles.billingState}>
                                        <IonSpinner />
                                        <p>{tModals('payment_plan.required.billing_loading')}</p>
                                    </div>
                                </IonItem>
                            ) : previewQuery.isError ? (
                                <IonItem>
                                    <div className={styles.billingState}>
                                        <IonText color="danger">
                                            <p>{(previewQuery.error as Error)?.message || tModals('payment_plan.required.billing_error')}</p>
                                        </IonText>
                                    </div>
                                </IonItem>
                            ) : inTrial ? (
                                <IonItem>
                                    <IonLabel className="ion-text-center">
                                        <h2>{tModals('payment_plan.required.trial_title')}</h2>
                                        <p>{tModals('payment_plan.required.trial_body', { count: trialDays })}</p>
                                    </IonLabel>
                                </IonItem>
                            ) : (
                                lineItems.map((line: any) => (
                                    <IonItem key={line.id}>
                                        <IonLabel>
                                            {line.description}
                                            <p>{line.type}</p>
                                        </IonLabel>
                                        <IonText slot="end" className={styles.billingAmount}>
                                            {formatAmount(line.amount, line.currency)}
                                        </IonText>
                                    </IonItem>
                                ))
                            )}
                        </IonList>
                    </div>
                </IonContent>
                <IonFooter className="ion-no-border">
                    <div className={styles.footer}>
                        {dueToday !== undefined && (
                            <div className={styles.dueRow}>
                                <span className={styles.dueLabel}>{tModals('payment_plan.required.due_today')}</span>
                                <span className={styles.dueAmount}>{formatAmount(dueToday, lineItems[0].currency)}</span>
                            </div>
                        )}
                        <IonButton
                            expand="block"
                            className={styles.checkoutButton}
                            onClick={() => handleCheckout()}
                            disabled={checkoutMutation.isPending}
                        >
                            {checkoutMutation.isPending ? <IonSpinner name="dots" /> : tModals('payment_plan.required.checkout')}
                        </IonButton>
                    </div>
                </IonFooter>
            </>
        );
    };

    const renderFeatureList = () => (
        <IonList className={styles.listCard}>
            {FEATURE_KEYS.map((key) => (
                <IonItem key={key}>
                    <span slot="start" className={styles.tickCheck}>
                        <IonIcon icon={checkmarkOutline} />
                    </span>
                    <IonLabel className="ion-text-wrap">{tModals(key)}</IonLabel>
                </IonItem>
            ))}
        </IonList>
    );

    // Lifetime and Active share the celebratory hero + feature list; only copy and footer differ.
    const renderSettledPlanContent = (isLifetime: boolean) => {
        const handleManagePaymentPlan = async () => {
            presentResult(async () => {
                const returnUrl = RETURN_URL_BASE + '/return' + (isNative ? '' : '?deviceId=' + deviceId);
                const result = await customerPortalMutation.mutateAsync({
                    deviceId,
                    returnUrl
                });

                if (result?.url) {
                    await Browser.open({
                        url: result.url,
                        windowName: "_self"
                    });
                } else {
                    console.error("Customer portal session creation failed", result);
                    throw new Error("Failed to create customer portal session");
                }
            });
        };

        const upsellPreview = lifetimeUpsellPreviewQuery.data;
        const upsellAmount = upsellPreview?.lineItems?.[0]?.amount;
        const upsellCurrency = upsellPreview?.lineItems?.[0]?.currency;
        const hasUpsellDiscount = upsellPreview?.originalAmount !== undefined
            && upsellPreview.originalAmount !== upsellAmount;
        const showUpsell = !isLifetime
            && !lifetimeUpsellPreviewQuery.isLoading
            && !lifetimeUpsellPreviewQuery.isError
            && upsellAmount !== undefined
            && upsellCurrency !== undefined;

        return (
            <>
                <IonContent color='light'>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
                        <ReactConfetti
                            width={dimensions.width}
                            height={dimensions.height}
                            recycle={true}
                            numberOfPieces={150}
                            gravity={0.1}
                            colors={CONFETTI_COLORS}
                        />
                    </div>
                    <div className={styles.wrapper}>
                        <div className={isLifetime ? styles.heroCard : `${styles.heroCard} ${styles.heroCardCompact}`}>
                            <span className={styles.sealCheck}>
                                <IonIcon icon={checkmarkOutline} />
                            </span>
                            <h2 className={styles.heroTitle}>
                                {tModals(isLifetime ? 'payment_plan.lifetime.title' : 'payment_plan.active.title')}
                            </h2>
                            <p className={`${styles.lead} ${styles.heroLead}`}>
                                {tModals(isLifetime ? 'payment_plan.lifetime.body' : 'payment_plan.active.body')}
                            </p>
                        </div>
                        <div className={styles.kicker}>
                            {tModals(isLifetime ? 'payment_plan.lifetime.included' : 'payment_plan.active.included')}
                        </div>
                        {renderFeatureList()}
                        {showUpsell && (
                            <>
                                <div className={styles.kicker}>{tModals('payment_plan.active.upsell_kicker')}</div>
                                <IonList className={styles.listCard}>
                                    <IonItem
                                        button
                                        detail={!checkoutMutation.isPending}
                                        lines="none"
                                        onClick={() => handleCheckout('lifetime')}
                                        disabled={checkoutMutation.isPending}
                                    >
                                        <span slot="start" className={styles.upsellIcon}>
                                            <IonIcon icon={infiniteOutline} />
                                        </span>
                                        <IonLabel className="ion-text-wrap">
                                            <span className={styles.upsellTitle}>{tModals('payment_plan.active.upsell_title')}</span>
                                            <span className={styles.upsellPriceRow}>
                                                {hasUpsellDiscount && (
                                                    <span className={styles.upsellPriceOld}>
                                                        {formatAmount(upsellPreview!.originalAmount!, upsellCurrency!)}
                                                    </span>
                                                )}
                                                <span className={styles.upsellPriceNew}>{formatAmount(upsellAmount!, upsellCurrency!)}</span>
                                            </span>
                                            <span className={styles.upsellSub}>{tModals('payment_plan.active.upsell_sub')}</span>
                                        </IonLabel>
                                        {checkoutMutation.isPending && <IonSpinner slot="end" name="dots" />}
                                    </IonItem>
                                </IonList>
                            </>
                        )}
                    </div>
                </IonContent>
                <IonFooter className="ion-no-border">
                    <div className={styles.footer}>
                        {isLifetime ? (
                            <IonButton
                                expand="block"
                                className={styles.checkoutButton}
                                onClick={() => dismiss(null, 'cancel')}
                            >
                                {tModals('payment_plan.lifetime.cta')}
                            </IonButton>
                        ) : (
                            <IonButton
                                expand="block"
                                className={styles.checkoutButton}
                                onClick={handleManagePaymentPlan}
                                disabled={customerPortalMutation.isPending}
                            >
                                <IonIcon slot="start" icon={cardOutline} />
                                {customerPortalMutation.isPending ? <IonSpinner name="dots" /> : tModals('payment_plan.active.manage')}
                            </IonButton>
                        )}
                    </div>
                </IonFooter>
            </>
        );
    };

    // Reviewing and Pending share the quiet reassurance layout; icon, pill, and copy differ.
    const renderWaitingContent = (variant: 'reviewing' | 'pending') => (
        <>
            <IonContent color='light'>
                <div className={styles.wrapper}>
                    <div className={styles.heroCard}>
                        <span className={`${styles.stateIcon} ${variant === 'reviewing' ? styles.stateIconWarning : styles.stateIconPrimary}`}>
                            <IonIcon icon={variant === 'reviewing' ? timeOutline : shieldCheckmarkOutline} />
                        </span>
                        {variant === 'reviewing' && (
                            <div>
                                <span className={styles.statusPill}>{tModals('payment_plan.reviewing.pill')}</span>
                            </div>
                        )}
                        <h2 className={styles.heroTitle}>{tModals(`payment_plan.${variant}.title`)}</h2>
                        <p className={`${styles.lead} ${styles.heroLead}`}>{tModals(`payment_plan.${variant}.body`)}</p>
                    </div>
                    <IonList className={`${styles.listCard} ${styles.noteCard}`}>
                        <IonItem lines="none">
                            <span slot="start" className={styles.notePlainCheck}>
                                <IonIcon icon={checkmarkOutline} />
                            </span>
                            <IonLabel className="ion-text-wrap">{tModals(`payment_plan.${variant}.note`)}</IonLabel>
                        </IonItem>
                    </IonList>
                </div>
            </IonContent>
            <IonFooter className="ion-no-border">
                <div className={styles.footer}>
                    <IonButton
                        expand="block"
                        className={styles.checkoutButton}
                        onClick={() => dismiss(null, 'cancel')}
                    >
                        {tModals(`payment_plan.${variant}.cta`)}
                    </IonButton>
                </div>
            </IonFooter>
        </>
    );

    const renderContent = () => {
        if (paymentPlanQuery.isLoading) {
            return (
                <IonContent>
                    <div className="ion-text-center ion-padding">
                        <IonSpinner />
                        <p>Loading payment plan information...</p>
                    </div>
                </IonContent>
            );
        }

        if (paymentPlanQuery.isError) {
            return (
                <IonContent>
                    <div className="ion-text-center ion-padding ion-color-danger">
                        <p>{(paymentPlanQuery.error as Error)?.message || 'Error loading payment plan'}</p>
                    </div>
                </IonContent>
            );
        }

        const plan = paymentPlanQuery.data;

        // No payment plan data / pending state: billing isn't open yet
        switch (plan?.state) {
            case PaymentPlanState.Required:
                return renderPendingPlanContent();
            case PaymentPlanState.Lifetime:
                return renderSettledPlanContent(true);
            case PaymentPlanState.Active:
                return renderSettledPlanContent(false);
            case PaymentPlanState.Reviewing:
                return renderWaitingContent('reviewing');
            default:
                return renderWaitingContent('pending');
        }
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Payment Plan</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss(null, 'cancel')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            {renderContent()}
        </IonPage>
    );
};

export default DevicePaymentPlanModal;
